import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';

const PasswordStrengthMeter = ({ password, onValidChange }) => {
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
  });
  const [strength, setStrength] = useState('weak');
  const [score, setScore] = useState(0);

  useEffect(() => {
    const checkRequirements = () => {
      const newReqs = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        digit: /\d/.test(password),
        special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
      };
      
      setRequirements(newReqs);
      
      // Calculate score
      let newScore = Object.values(newReqs).filter(Boolean).length;
      if (password.length >= 12) newScore += 0.5;
      if (password.length >= 16) newScore += 0.5;
      newScore = Math.min(newScore, 5);
      setScore(newScore);
      
      // Determine strength
      let newStrength = 'weak';
      if (newScore >= 5) newStrength = 'strong';
      else if (newScore >= 3) newStrength = 'medium';
      setStrength(newStrength);
      
      // Check if all requirements are met
      const isValid = Object.values(newReqs).every(Boolean);
      if (onValidChange) {
        onValidChange(isValid);
      }
    };
    
    checkRequirements();
  }, [password, onValidChange]);

  const getStrengthColor = () => {
    switch (strength) {
      case 'strong': return '#22C55E';
      case 'medium': return '#F59E0B';
      default: return '#EF4444';
    }
  };

  const getStrengthWidth = () => {
    return `${(score / 5) * 100}%`;
  };

  const RequirementItem = ({ met, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-gray-300" />
      )}
      <span className={met ? 'text-green-700' : 'text-gray-500'}>
        {label}
      </span>
    </div>
  );

  if (!password) return null;

  return (
    <div className="space-y-3 mt-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Password Strength</span>
          <span 
            className="font-medium capitalize"
            style={{ color: getStrengthColor() }}
          >
            {strength}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full transition-all duration-300 rounded-full"
            style={{ 
              width: getStrengthWidth(),
              backgroundColor: getStrengthColor()
            }}
          />
        </div>
      </div>
      
      {/* Requirements Checklist */}
      <div className="grid grid-cols-2 gap-1 p-3 bg-gray-50 rounded-lg">
        <RequirementItem met={requirements.length} label="8+ characters" />
        <RequirementItem met={requirements.uppercase} label="Uppercase letter" />
        <RequirementItem met={requirements.lowercase} label="Lowercase letter" />
        <RequirementItem met={requirements.digit} label="Number" />
        <RequirementItem met={requirements.special} label="Special character" />
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
